// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useRef, useCallback, type FC } from 'react'
import { Input, Label, Badge, Button } from '@/components/ui'
import { X, Plus } from 'lucide-react'
import type { DatasourceConfigProps, MarylandBillMasterListSettings } from '../types'

const DEFAULT_SETTINGS: MarylandBillMasterListSettings = {
  enabledSessions: [],
  maxBills: 100,
}

/**
 * Configuration form for MarylandBillMasterList datasource.
 * Allows configuring legislative sessions and bill limits.
 */
export const MarylandBillMasterListConfig: FC<DatasourceConfigProps<MarylandBillMasterListSettings>> = ({
  config,
  onChange,
}) => {
  // Stable ref for callback to avoid restarting effect when parent re-renders
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [enabledSessions, setEnabledSessions] = useState<string[]>(
    config?.enabledSessions ?? DEFAULT_SETTINGS.enabledSessions
  )
  const [maxBills, setMaxBills] = useState<number>(config?.maxBills ?? DEFAULT_SETTINGS.maxBills)

  const [newSession, setNewSession] = useState('')
  const [sessionError, setSessionError] = useState<string | null>(null)

  // Sync state changes to parent via onChange callback
  useEffect(() => {
    onChangeRef.current?.('enabledSessions', enabledSessions)
  }, [enabledSessions])

  useEffect(() => {
    onChangeRef.current?.('maxBills', maxBills)
  }, [maxBills])

  // Validate session format (YYYYRS)
  const validateSession = (session: string): boolean => {
    return /^\d{4}RS$/i.test(session)
  }

  const handleAddSession = useCallback(() => {
    const trimmed = newSession.trim().toUpperCase()
    if (!trimmed) return

    if (!validateSession(trimmed)) {
      setSessionError('Session must be in YYYYRS format (e.g., 2024RS)')
      return
    }

    if (enabledSessions.includes(trimmed)) {
      setSessionError('Session already added')
      return
    }

    setSessionError(null)
    setEnabledSessions((prev) => [...prev, trimmed])
    setNewSession('')
  }, [newSession, enabledSessions])

  const handleRemoveSession = useCallback((session: string) => {
    setEnabledSessions((prev) => prev.filter((s) => s !== session))
  }, [])

  const handleMaxBillsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.valueAsNumber
    setMaxBills(isNaN(num) ? 0 : num)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSession()
    }
  }

  return (
    <div className="space-y-6">
      {/* Enabled Sessions */}
      <div className="space-y-3">
        <Label htmlFor="sessions">Legislative Sessions</Label>
        <p className="text-xs text-muted-foreground">
          Sessions to sync, in YYYYRS format (e.g., 2024RS, 2025RS)
        </p>

        {/* Session badges */}
        <div className="flex flex-wrap gap-2">
          {enabledSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No sessions configured</p>
          ) : (
            enabledSessions.map((session) => (
              <Badge key={session} variant="secondary" className="gap-1">
                {session}
                <button
                  onClick={() => handleRemoveSession(session)}
                  className="ml-1 hover:text-destructive"
                  aria-label={`Remove ${session}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))
          )}
        </div>

        {/* Add session input */}
        <div className="flex gap-2">
          <Input
            id="sessions"
            placeholder="2025RS"
            value={newSession}
            onChange={(e) => {
              setNewSession(e.target.value)
              setSessionError(null)
            }}
            onKeyDown={handleKeyDown}
            className="w-32"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddSession}
            disabled={!newSession.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        {sessionError && <p className="text-xs text-destructive">{sessionError}</p>}
      </div>

      {/* Max Bills */}
      <div className="space-y-2">
        <Label htmlFor="max-bills">Max Bills per Session</Label>
        <p className="text-xs text-muted-foreground">
          Maximum number of bills to fetch per session (0 for unlimited)
        </p>
        <Input
          id="max-bills"
          type="number"
          min={0}
          value={maxBills || ''}
          onChange={handleMaxBillsChange}
          className="w-32"
        />
      </div>
    </div>
  )
}
